import { useState, useRef, useEffect } from 'react';
import { Info, X } from 'lucide-react';

interface ClassificationBadgeProps {
    code: number;
    label: string;
    dateLastCall?: string | null;
    companyCode?: string;
}

const CLASSIFICATION_STYLES: Record<string, { bg: string; text: string; dot: string }> = {
    Green: { bg: 'bg-green-50', text: 'text-green-700', dot: 'bg-green-500' },
    Yellow: { bg: 'bg-yellow-50', text: 'text-yellow-700', dot: 'bg-yellow-500' },
    Red: { bg: 'bg-red-50', text: 'text-red-700', dot: 'bg-red-500' },
    'N/A (EOS)': { bg: 'bg-slate-50', text: 'text-slate-500', dot: 'bg-slate-300' },
    'Not classified': { bg: 'bg-gray-50', text: 'text-gray-600', dot: 'bg-gray-400' },
    Unknown: { bg: 'bg-gray-50', text: 'text-gray-600', dot: 'bg-gray-400' },
};

function formatDate(dateStr: string): string {
    return new Date(dateStr).toLocaleDateString('de-CH');
}

function buildContextReason(label: string, dateLastCall?: string | null, companyCode?: string): string {
    switch (label) {
        case 'Green':
            return dateLastCall
                ? `DateLastCall is ${formatDate(dateLastCall)} — within the next 2 months. This order is active and recently attended to.`
                : 'DateLastCall is in the future and within 2 months.';
        case 'Yellow':
            return dateLastCall
                ? `DateLastCall is ${formatDate(dateLastCall)} — more than 2 months away. This order needs attention soon.`
                : 'DateLastCall is in the future but more than 2 months away.';
        case 'Red':
            return dateLastCall
                ? `DateLastCall was ${formatDate(dateLastCall)} — already in the past. This order has expired and requires immediate follow-up.`
                : 'DateLastCall is in the past. Immediate follow-up required.';
        case 'N/A (EOS)':
            return `Company "${companyCode || '?'}" uses EOS (cloud Business Central), which does not provide the DateLastCall field. Classification is not available for EOS companies.`;
        case 'Not classified':
            return `No DateLastCall is recorded for this order${companyCode ? ` (company: ${companyCode})` : ''}. Without this date, the classification cannot be determined.`;
        default:
            return `Classification code ${label} is outside the expected range. This may indicate a data issue.`;
    }
}

export function ClassificationBadge({ label, dateLastCall, companyCode }: ClassificationBadgeProps) {
    const [showInfo, setShowInfo] = useState(false);
    const popoverRef = useRef<HTMLDivElement>(null);

    const style = CLASSIFICATION_STYLES[label] || CLASSIFICATION_STYLES.Unknown;
    const reason = buildContextReason(label, dateLastCall, companyCode);

    useEffect(() => {
        if (!showInfo) return;

        function handleClickOutside(e: MouseEvent) {
            if (popoverRef.current && !popoverRef.current.contains(e.target as Node)) {
                setShowInfo(false);
            }
        }

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [showInfo]);

    return (
        <span className="relative inline-flex items-center gap-1">
            <span
                className={`inline-flex cursor-pointer items-center gap-1.5 rounded-full px-2 py-0.5 text-xs font-medium ${style.bg} ${style.text}`}
                onClick={(e) => {
                    e.stopPropagation();
                    setShowInfo(!showInfo);
                }}
                title="Click for classification details"
            >
                <span className={`h-1.5 w-1.5 rounded-full ${style.dot}`} />
                {label}
                <Info className="h-3 w-3 opacity-50" />
            </span>

            {showInfo && (
                <div
                    ref={popoverRef}
                    className="absolute left-0 top-full z-50 mt-1 w-72 rounded-lg border border-gray-200 bg-white p-3 shadow-lg"
                    onClick={(e) => e.stopPropagation()}
                >
                    <div className="mb-2 flex items-center justify-between">
                        <h4 className="text-xs font-semibold text-gray-900">
                            Classification: {label}
                        </h4>
                        <button
                            onClick={() => setShowInfo(false)}
                            className="rounded p-0.5 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
                        >
                            <X className="h-3.5 w-3.5" />
                        </button>
                    </div>
                    <p className="text-xs leading-relaxed text-gray-600">{reason}</p>
                    <div className="mt-2 border-t border-gray-100 pt-2">
                        <p className="text-[10px] leading-relaxed text-gray-400">
                            Based on DateLastCall from Business Central. RED companies (bin, dbi) use dynamic classification. EOS companies lack this field.
                        </p>
                    </div>
                </div>
            )}
        </span>
    );
}
